import React, { useEffect, useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { HoaReturn, HoaMessageRequest } from '../clients/rust-api-client';
import { Button } from '../common/Catalyst/button';
import { Dialog, DialogBody, DialogTitle } from '../common/Catalyst/dialog';
import { Divider } from '../common/Catalyst/divider';
import {
  ErrorMessage,
  Field,
  FieldGroup,
  Fieldset,
  Label,
} from '../common/Catalyst/fieldset';
import { Input } from '../common/Catalyst/input';
import { Textarea } from '../common/Catalyst/textarea';
import { ErrorAlert } from '../common/Custom/ErrorAlert';
import { apiErrorMessage } from '../utils/apiErrors';

interface SendHOAMessageDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSend: (message: HoaMessageRequest) => Promise<void>;
  hoa: HoaReturn;
  senderName: string;
}

interface FormErrors {
  [key: string]: string | undefined;
}

export const SendHOAMessageDialog: React.FC<SendHOAMessageDialogProps> = ({
  isOpen,
  onClose,
  onSend,
  hoa,
  senderName,
}) => {
  const [formData, setFormData] = useState<HoaMessageRequest>({
    hoa_id: hoa.id,
    subject: '',
    message_body: '',
    sender_name: senderName,
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [isSending, setIsSending] = useState(false);
  // Why the send failed. The dialog stays open with this in it, so the
  // message is not lost to a failure the person cannot see.
  const [sendError, setSendError] = useState('');

  useEffect(() => {
    setFormData({
      hoa_id: hoa.id,
      subject: '',
      message_body: '',
      sender_name: senderName,
    });
    setErrors({});
    setTouched({});
    setSendError('');
  }, [hoa, senderName, isOpen]);

  const validateField = (
    name: keyof HoaMessageRequest,
    value: string | number
  ): string | undefined => {
    if (typeof value === 'number') return undefined;

    switch (name) {
      case 'subject':
        return !value.trim() ? 'Subject is required' : undefined;
      case 'message_body':
        return !value.trim() ? 'Message is required' : undefined;
      default:
        return undefined;
    }
  };

  const isValid = (): boolean => {
    const subjectError = validateField('subject', formData.subject);
    const messageError = validateField('message_body', formData.message_body);

    return !subjectError && !messageError;
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setTouched((prev) => ({ ...prev, [name]: true }));

    const error = validateField(name as keyof HoaMessageRequest, value);
    setErrors((prev) => ({ ...prev, [name]: error }));
  };

  const handleBlur = (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
  };

  const handleSend = async () => {
    if (isValid()) {
      try {
        setIsSending(true);
        setSendError('');
        await onSend(formData);
        onClose();
      } catch (error) {
        console.error('Error sending message:', error);
        setSendError(apiErrorMessage(error, "Couldn't send the message."));
      } finally {
        setIsSending(false);
      }
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      className="w-full max-w-2xl mx-auto"
    >
      <div className="relative flex flex-col h-full">
        <div className="p-2 md:p-4">
          <DialogTitle className="text-xl font-semibold">
            Send Message to All Members of {hoa.name}
          </DialogTitle>
          <button
            onClick={onClose}
            className="absolute right-4 top-4 p-2 hover:bg-zinc-100 dark:hover:bg-zinc-700 rounded-full transition-colors duration-150"
            aria-label="Close dialog"
          >
            <XMarkIcon className="h-5 w-5 text-zinc-600 dark:text-zinc-100" />
          </button>

          <Divider className="my-4" />

          <DialogBody>
            <Fieldset>
              <FieldGroup>
                <Field>
                  <Label htmlFor="subject">Subject*</Label>
                  <Input
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    placeholder="Enter message subject"
                    required
                    data-invalid={touched.subject && errors.subject}
                  />
                  {touched.subject && errors.subject && (
                    <ErrorMessage>{errors.subject}</ErrorMessage>
                  )}
                </Field>

                <Field>
                  <Label htmlFor="message_body">Message*</Label>
                  <Textarea
                    id="message_body"
                    name="message_body"
                    value={formData.message_body}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    placeholder="Enter your message to all HOA members"
                    rows={8}
                    required
                    data-invalid={touched.message_body && errors.message_body}
                  />
                  {touched.message_body && errors.message_body && (
                    <ErrorMessage>{errors.message_body}</ErrorMessage>
                  )}
                </Field>

                <div className="text-sm text-gray-600 dark:text-gray-400">
                  This message will be sent to all members of {hoa.name}
                </div>
              </FieldGroup>
            </Fieldset>
            <ErrorAlert className="mt-4">{sendError}</ErrorAlert>
          </DialogBody>
        </div>

        <div className="sticky bottom-0 mt-auto border-t border-gray-200 dark:border-gray-700 bg-stone-100 dark:bg-gray-800 p-4 md:p-6">
          <div className="flex flex-col space-y-3">
            <Button
              onClick={handleSend}
              color="sky"
              disabled={!isValid() || isSending}
              className="w-full disabled:opacity-50"
            >
              {isSending ? 'Sending...' : 'Send to All Members'}
            </Button>
            <Button onClick={onClose} color="zinc" className="w-full">
              Cancel
            </Button>
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export default SendHOAMessageDialog;
